import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import ServicesHub from "@/pages/ServicesHub";
import FireAlarms from "@/pages/FireAlarms";
import EmergencyLighting from "@/pages/EmergencyLighting";
import FireExtinguishers from "@/pages/FireExtinguishers";
import TestingCertification from "@/pages/TestingCertification";
import Maintenance from "@/pages/Maintenance";
import RepairsUpgrades from "@/pages/RepairsUpgrades";
import CCTVSecurity from "@/pages/CCTVSecurity";
import AccessControl from "@/pages/AccessControl";
import Monitoring from "@/pages/Monitoring";
import MaintenanceSupport from "@/pages/MaintenanceSupport";
import Industries from "@/pages/Industries";
import FAQs from "@/pages/FAQs";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import TermsConditions from "@/pages/TermsConditions";
import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    const lenis = lenisRef.current;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        if (lenis) lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />

            {/* Fire Safety division */}
            <Route path="/services" element={<ServicesHub />} />
            <Route path="/services/fire-alarms" element={<FireAlarms />} />
            <Route path="/services/emergency-lighting" element={<EmergencyLighting />} />
            <Route path="/services/fire-extinguishers" element={<FireExtinguishers />} />
            <Route path="/services/testing-certification" element={<TestingCertification />} />
            <Route path="/services/maintenance" element={<Maintenance />} />
            <Route path="/services/repairs-upgrades" element={<RepairsUpgrades />} />

            {/* Other divisions */}
            <Route path="/cctv-security" element={<CCTVSecurity />} />
            <Route path="/access-control" element={<AccessControl />} />
            <Route path="/monitoring" element={<Monitoring />} />
            <Route path="/maintenance-support" element={<MaintenanceSupport />} />

            {/* Legacy redirects */}
            <Route path="/services/fire-safety" element={<Navigate to="/services/fire-alarms" replace />} />
            <Route path="/services/cctv" element={<Navigate to="/cctv-security" replace />} />
            <Route path="/services/access-control" element={<Navigate to="/access-control" replace />} />
            <Route path="/maintenance" element={<Navigate to="/services/maintenance" replace />} />
            <Route path="/maintenance-compliance" element={<Navigate to="/services/maintenance" replace />} />

            <Route path="/industries" element={<Industries />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
          </Route>

          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/leads" element={<AdminLeads />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-center px-4">
        <div className="fss-overline">404</div>
        <h1 className="mt-3 font-display text-4xl font-semibold text-slate-900">
          Page not found
        </h1>
        <a href="/" className="mt-6 inline-block fss-btn fss-btn-primary">Back to home</a>
      </div>
    </div>
  );
}

export default App;
